import { useEffect, useMemo, useState } from 'react';
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Input, Select, SelectItem,
} from '@heroui/react';
import { toast } from 'sonner';
import { metalFinishesFor, type MetalType } from '@estimator/shared';
import { useCreateLineItem, useUpdateLineItem, useLineItem } from '@/api/line-items';
import { inchesToFeetDisplay } from '@/utils/math-input';
import { calcMetalBaseCostPreview, type MetalBaseConfig } from '@/utils/metal-base-cost';

export interface MetalBaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  areaId: string;
  projectId: string;
  templateMode?: boolean;
  existingLineItemId?: string | null;
  existingConfig?: string | null;
}

const money = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EMPTY: MetalBaseConfig = {
  product_type: 'metal_base',
  width: 120,
  depth: 18,
  height: 20,
  cost_per_lf: 0,
  metal_type: 'stainless',
  finish: 'polished',
  paint_cost_per_lf: null,
};

/**
 * A Metal Base component template: a run of fabricated metal priced by the
 * linear foot, where linear feet come from Width.
 *
 * Stainless finishes specify what the metal is and cost nothing extra. A
 * painted finish flagged `requiresCost` reveals its own paint rate per linear
 * foot, which adds to the total. See
 * docs/superpowers/specs/2026-08-04-cria-metal-base-template-design.md
 */
export default function MetalBaseModal({
  isOpen, onClose, areaId, templateMode = false,
  existingLineItemId = null, existingConfig = null,
}: MetalBaseModalProps) {
  const createLineItem = useCreateLineItem();
  const updateLineItem = useUpdateLineItem();
  // Callers pass only the id and the config blob, so the row's own code and
  // description have to be fetched or an edit would blank them.
  const { data: existingItem } = useLineItem(isOpen ? existingLineItemId : null);

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [config, setConfig] = useState<MetalBaseConfig>(EMPTY);
  const [saving, setSaving] = useState(false);

  // Load the row being edited; reset to blank for a new one.
  useEffect(() => {
    if (!isOpen) return;
    if (existingConfig) {
      try {
        setConfig({ ...EMPTY, ...JSON.parse(existingConfig) });
      } catch {
        toast.error('This template’s metal base config could not be read');
        setConfig(EMPTY);
      }
    } else {
      setConfig(EMPTY);
      setCode('');
      setDescription('');
    }
  }, [isOpen, existingConfig]);

  // Code and description arrive with the fetched row, a tick after the config.
  useEffect(() => {
    if (!isOpen || !existingItem) return;
    setCode((existingItem as any).cabinet_code ?? '');
    setDescription((existingItem as any).description ?? '');
  }, [isOpen, existingItem]);

  const setField = <K extends keyof MetalBaseConfig>(k: K, v: MetalBaseConfig[K]) =>
    setConfig((c) => ({ ...c, [k]: v }));

  const finishOptions = useMemo(
    () => metalFinishesFor(config.metal_type),
    [config.metal_type],
  );

  const selectedFinish = useMemo(
    () => finishOptions.find((f) => f.key === config.finish) ?? null,
    [finishOptions, config.finish],
  );

  const cost = useMemo(() => calcMetalBaseCostPreview(config), [config]);

  /**
   * Switching metal type moves the finish to the new type's first option. A
   * stainless key is not a valid painted key and vice versa, so carrying the
   * old one over would leave the config referencing a finish that does not
   * exist for its type.
   */
  const handleMetalTypeChange = (next: MetalType) =>
    setConfig((c) => ({
      ...c,
      metal_type: next,
      finish: metalFinishesFor(next)[0]?.key ?? null,
      paint_cost_per_lf: next === 'painted' ? (c.paint_cost_per_lf ?? 0) : null,
    }));

  const handleSave = async () => {
    if (!description.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    // Dimensions are written to the row's own columns as well as the config so
    // the library's W × H × D column renders. The config stays the source of
    // truth for this modal.
    const body: any = {
      cabinet_code: code.trim() || 'MB',
      description: description.trim(),
      quantity: 1,
      width: config.width,
      height: config.height,
      depth: config.depth,
      metal_base_config: JSON.stringify(config),
      is_template: templateMode || !areaId,
      area_id: areaId || null,
    };
    try {
      if (existingLineItemId) {
        await updateLineItem.mutateAsync({ id: existingLineItemId, ...body });
        toast.success('Metal base updated');
      } else {
        await createLineItem.mutateAsync(body);
        toast.success('Metal base created');
      }
      onClose();
    } catch {
      toast.error('Failed to save metal base');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{ base: 'bg-[var(--bg-surface)]', header: 'border-b border-[var(--border)]' }}
    >
      <ModalContent>
        <ModalHeader className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {existingLineItemId ? 'Edit Metal Base' : 'New Metal Base'}
        </ModalHeader>

        <ModalBody className="py-5 flex flex-col gap-5">
          {/* Cost bar */}
          <div
            className="rounded-lg border p-3 flex flex-wrap items-baseline gap-x-6 gap-y-1"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-panel)' }}
          >
            {([
              ['Metal', money(cost.metal_cost)],
              ['Paint', money(cost.paint_cost)],
              ['Total', money(cost.total_cost)],
              ['Rate', cost.rate_per_lf == null ? '—' : `${money(cost.rate_per_lf)}/LF`],
              ['LF', cost.linear_feet.toFixed(2)],
            ] as Array<[string, string]>).map(([label, value]) => (
              <span key={label} className="inline-flex items-baseline">
                <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>
                  {label}
                </span>
                <span
                  className="text-xs font-semibold ml-1.5"
                  style={{ color: label === 'Total' ? '#e67e22' : 'var(--text-primary)' }}
                >
                  {value}
                </span>
              </span>
            ))}
          </div>

          {/* Identity */}
          <div className="grid grid-cols-3 gap-3">
            <Input
              size="sm" label="Code" value={code} onValueChange={setCode}
              placeholder="MB"
              classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            />
            <Input
              size="sm" label="Name" isRequired className="col-span-2"
              value={description} onValueChange={setDescription}
              placeholder="e.g. Brushed stainless banquette base"
              classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            />
          </div>

          {/* Dimensions */}
          <div className="flex flex-col gap-2">
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: 'var(--text-secondary)' }}
            >
              Dimensions
            </span>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex flex-col gap-1">
                <Input
                  size="sm" type="number" step="0.125" min="0" label="Width"
                  endContent={<span className="text-xs" style={{ color: 'var(--text-muted)' }}>in</span>}
                  value={String(config.width ?? 0)}
                  onValueChange={(v) => setField('width', parseFloat(v) || 0)}
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {inchesToFeetDisplay(config.width ?? 0)}
                </span>
              </div>
              <Input
                size="sm" type="number" step="0.125" min="0" label="Depth"
                endContent={<span className="text-xs" style={{ color: 'var(--text-muted)' }}>in</span>}
                value={String(config.depth ?? 0)}
                onValueChange={(v) => setField('depth', parseFloat(v) || 0)}
                classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
              />
              <Input
                size="sm" type="number" step="0.125" min="0" label="Height"
                endContent={<span className="text-xs" style={{ color: 'var(--text-muted)' }}>in</span>}
                value={String(config.height ?? 0)}
                onValueChange={(v) => setField('height', parseFloat(v) || 0)}
                classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
              />
            </div>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Width sets the linear footage that drives cost. Depth and height are recorded
              for spec and drawings only.
            </span>
          </div>

          {/* Cost */}
          <Input
            size="sm" type="number" step="0.01" min="0"
            label="Cost per linear foot (USD)"
            value={String(config.cost_per_lf ?? 0)}
            onValueChange={(v) => setField('cost_per_lf', parseFloat(v) || 0)}
            classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
          />

          {/* Finish */}
          <div className="flex flex-col gap-3">
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: 'var(--text-secondary)' }}
            >
              Finish
            </span>
            <div className="grid grid-cols-2 gap-3">
              <Select
                size="sm"
                label="Metal type"
                disallowEmptySelection
                selectedKeys={[config.metal_type]}
                onSelectionChange={(keys) => {
                  const v = Array.from(keys)[0];
                  if (v) handleMetalTypeChange(v as MetalType);
                }}
                classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
              >
                <SelectItem key="stainless" textValue="Stainless Steel">Stainless Steel</SelectItem>
                <SelectItem key="painted" textValue="Painted Metal">Painted Metal</SelectItem>
              </Select>

              <Select
                size="sm"
                label={config.metal_type === 'painted' ? 'Paint finish' : 'Stainless finish'}
                disallowEmptySelection
                selectedKeys={config.finish ? [config.finish] : []}
                onSelectionChange={(keys) => {
                  const v = Array.from(keys)[0];
                  setField('finish', v ? String(v) : null);
                }}
                classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
              >
                {finishOptions.map((f) => (
                  <SelectItem key={f.key} textValue={f.label}>{f.label}</SelectItem>
                ))}
              </Select>
            </div>

            {config.metal_type === 'painted' && selectedFinish?.requiresCost && (
              <Input
                size="sm" type="number" step="0.01" min="0"
                label="Paint cost per linear foot (USD)"
                value={String(config.paint_cost_per_lf ?? 0)}
                onValueChange={(v) => setField('paint_cost_per_lf', parseFloat(v) || 0)}
                classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
              />
            )}

            {config.metal_type === 'stainless' && (
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Stainless finishes specify what the metal is and do not change the cost.
              </span>
            )}
          </div>

          {cost.note && (
            <p className="text-[11px] leading-snug" style={{ color: 'var(--text-muted)' }}>
              {cost.note}
            </p>
          )}
        </ModalBody>

        <ModalFooter>
          <Button size="sm" variant="light" onPress={onClose}>Cancel</Button>
          <Button
            size="sm" color="primary" isLoading={saving}
            isDisabled={!description.trim()}
            onPress={handleSave}
          >
            {existingLineItemId ? 'Save changes' : 'Create metal base'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
